import { captchaAxios, masterAxios } from 'api/config'
import { Chat } from 'types/chat'
import { store } from 'store/index'
import { messagesActions, messagesSelectors, selectChatId } from 'store/messages'
import { ChatMessage } from 'types/chat-message'
import axios from 'axios'
import { settingsActions, settingsSelectors } from 'store/settings'
import { isEmpty, map } from 'lodash'
import { UploadService } from '@runelab/odin-common'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'
import { useQuery } from '@tanstack/react-query'
import { showErrorToast } from 'utils/toast'
import i18n from 'i18next'
import { queryClient } from 'providers/QueryProvider'

export const CHAT_QUERY_KEY = 'chat'

type OpenNewChatParams = {
  areaId?: string
  /** Notebook mode: lock the new chat to this single agent. */
  agentId?: string
  /** Notebook mode: host entity the notebook is about; echoed by the server, used to scope history. */
  contextId?: string
}

export const openNewChat = async ({ areaId, agentId, contextId }: OpenNewChatParams = {}) => {
  const { dispatch } = store
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  const isMultiArea = settingsSelectors.isMultiAreaFlow(state)
  const lang = settingsSelectors.lang(state)

  const httpClient = isLogged ? axios : captchaAxios
  const requestAreaId = isMultiArea ? undefined : areaId

  try {
    const { data: chat } = await httpClient.post<Chat>('chats/bot', {
      culture: lang,
      areaId: requestAreaId,
      agentId,
      contextId,
    })
    console.debug({ chat })
    dispatch(messagesActions.addChat(chat))
    dispatch(messagesActions.setChatId(chat._id))
    if (agentId) queryClient.invalidateQueries({ queryKey: [NOTEBOOK_HISTORY_QUERY_KEY] }).catch(console.error)

    analytics.track(ANALYTICS_EVENTS.CHAT_OPEN, {
      chat_type: isLogged ? 'logged' : 'guest',
      chat_area_id: requestAreaId ?? chat.areaIds?.[0],
      chat_user_id: chat.user?._id,
    })
  } catch (e) {
    console.error({ e })
    if (isMultiArea) showErrorToast(i18n.t('multiAreaChatError'))
    return Promise.reject(e)
  }
}

export const getChatMessages = async (chatId: string) => {
  try {
    const { data } = await masterAxios.get<ChatMessage[]>('chat-messages', {
      params: {
        chatId,
        sort: 'createdAt',
        limit: 100,
      },
    })
    store.dispatch(messagesActions.setChatMessages({ chatId, messages: data }))
    return data
  } catch (e) {
    console.error({ e })
    return []
  }
}

export const NOTEBOOK_HISTORY_QUERY_KEY = 'notebook-history'

/** Notebook history: the caller's chats locked to `agentId` (and, when given, about `contextId`), newest first. */
export const useGetNotebookHistory = (agentId: string, contextId?: string) =>
  useQuery({
    queryKey: [NOTEBOOK_HISTORY_QUERY_KEY, agentId, contextId],
    queryFn: async () =>
      (await axios.get<Chat[]>('chats/me', { params: { agentId, contextId, sort: '-createdAt', limit: 20 } })).data,
  })

export const getMyChats = async ({ areaId }: { areaId?: string }) => {
  try {
    const { data } = await axios.get<Chat[]>('chats/me', { params: { areaIds: areaId, limit: 100 } })
    store.dispatch(messagesActions.addChats(data))

    const chatId = selectChatId(store.getState())

    const chatIndex = data.findIndex((chat) => chat._id === chatId)

    if (chatIndex === -1) {
      // The open chat is gone: clear the selection (per-chat status needs no separate reset).
      store.dispatch(messagesActions.setChatId(undefined))
      store.dispatch(settingsActions.setTicketReminderVisible(false))
    }
  } catch (e) {
    console.error({ e })
  }
}

type MessageToOperatorParams = {
  message: string
  files: File[]
}

const createUploads = async (files: File[]) => {
  const service = new UploadService(axios)
  return await Promise.all(
    files.map((file) => {
      return service.uploadFile({
        file,
        uploadFileParam: {
          additionalInfo: {},
          linkedEntityId: '',
          linkedEntityType: '',
        },
      })
    }),
  )
}

export const sentMessageToOperator = async ({ message, files }: MessageToOperatorParams) => {
  const state = store.getState()
  const chatId = selectChatId(state)

  analytics.track(ANALYTICS_EVENTS.MESSAGE_SENT, {
    mode: 'operator',
    source: 'typed',
    attachment_count: files.length,
    message_count: messagesSelectors.userMessageCount(state) + 1,
  })

  try {
    const uploads = await createUploads(files)
    console.debug({ uploads })
    const { data } = await axios.post('chat-messages', {
      message,
      chatId,
      operatorAttachmentIds: map(uploads, '_id'),
    })
    console.debug({ newMessage: data })
    store.dispatch(messagesActions.addIncomingMessage(data))
  } catch (e) {
    console.error({ e })
  }
}

export const assignGuestChatsToCurrentUser = async () => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  const guestChats = messagesSelectors.guestChats(state)

  const chatIds = map(guestChats, '_id')
  console.debug({ guestChats })

  if (isLogged) {
    if (isEmpty(chatIds)) {
      return Promise.resolve()
    }
    await axios.put('chats/assign/me', {
      chatIds: map(guestChats, '_id'),
    })
    await getMyChats({})
  } else {
    return Promise.resolve()
  }
}

export const markChatAsRead = async (chatId: string) => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)

  // Skip if user is not logged in (guest users are not in participants)
  if (!isLogged) return

  try {
    const { data } = await captchaAxios.patch<Chat>(`chats/${chatId}/read`)
    store.dispatch(messagesActions.editChat(data))
  } catch (e) {
    console.error({ e })
  }
}

export const exportChat = async (chatId: string, email: string) => {
  try {
    await axios.get(`chats/${chatId}/export/${email}`)
    analytics.track(ANALYTICS_EVENTS.CHAT_EXPORTED)
  } catch (e) {
    console.error({ e })
  }
}

export const getChat = async (chatId: string): Promise<Chat> => {
  const { data } = await axios.get<Chat>(`chats/${chatId}`)
  store.dispatch(messagesActions.setChat(data))
  return data
}

export const useGetChat = (chatId: string | undefined) => {
  return useQuery({
    queryKey: [CHAT_QUERY_KEY, chatId],
    queryFn: () => getChat(chatId!),
    enabled: !!chatId,
    gcTime: 0,
    staleTime: 0, // Consider data fresh for 30 seconds
  })
}
